import { useState } from "react";
import { BaseItem as ZhBaseItem, Dialog as ZhDialog, ZhMap } from "zhihao-ui-react";

import { areaLib } from "@/utils/areaLib";

export interface ProductDetailLocationData {
    provinceCode?: string;
    cityCode?: string;
    regionCode?: string;
    address?: string;
    geo?: { lng?: number | string; lat?: number | string };
}

interface ProductDetailMapDialogProps {
    location: ProductDetailLocationData;
    span?: number;
    labelWidth?: number;
}

/** 将 Vue geo 对象转换成 zhihao 地图需要的经纬度中心点。 */
function parseMapCenter(location: ProductDetailLocationData): [number, number] | undefined {
    const lng = Number(location.geo?.lng);
    const lat = Number(location.geo?.lat);
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : undefined;
}

/** 保留 Vue 详情弹窗中的 lng,lat 文本顺序，避免地图和详情字段语义不一致。 */
function formatCoordinates(location: ProductDetailLocationData): string {
    if (location.geo?.lng == null || location.geo?.lat == null) return "--";
    return `${location.geo.lng},${location.geo.lat}`;
}

/** 商品详情提货地点，复用 Vue 的地址字段并提供只读地图查看入口。 */
export function ProductDetailMapDialog({ location, span = 3, labelWidth = 112 }: ProductDetailMapDialogProps) {
    const [open, setOpen] = useState(false);
    const center = parseMapCenter(location);
    const areaCodes = [location.provinceCode, location.cityCode, location.regionCode].filter(Boolean).join("/");
    const areaName = areaLib.getFullAreaName(location.provinceCode, location.cityCode, location.regionCode);
    const locationText = [areaName, location.address].filter(Boolean).join(" ") || "--";
    const canViewMap = Boolean(areaCodes && center);
    const coordinates = formatCoordinates(location);

    return (
        <>
            <ZhBaseItem colon={false} label="提货地点" labelWidth={labelWidth} span={span}>
                {canViewMap ? (
                    <button
                        aria-label="查看提货地点地图"
                        className="product-detail-map-trigger inline-flex items-center text-left"
                        type="button"
                        onClick={() => setOpen(true)}
                    >
                        <span>
                            <span className="product-detail-compat-location">{areaCodes} </span>
                            {locationText}
                        </span>
                        <i aria-hidden="true" className="iconfont icon-map lngLat ml-8" />
                    </button>
                ) : (
                    <span>
                        <span className="product-detail-compat-location">{areaCodes} </span>
                        {locationText}
                    </span>
                )}
            </ZhBaseItem>
            <ZhDialog
                open={open}
                title="查看位置"
                width="920px"
                closeOnClickModal={false}
                destroyOnClose
                showClose
                onCancel={() => setOpen(false)}
            >
                <section aria-label="商品提货地点地图" className="relative h-[482px] w-full">
                    <ZhMap center={center} className="h-full w-full" layerType="satellite" showScale />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="flex flex-col items-center">
                            <span aria-label="地图位置标记" className="text-3xl text-red-600">
                                ●
                            </span>
                            <span aria-label="地图中心坐标" className="rounded bg-white/90 px-2 py-1 shadow">
                                {coordinates}
                            </span>
                        </div>
                    </div>
                </section>
            </ZhDialog>
        </>
    );
}

export default ProductDetailMapDialog;
